import { useCallback, useEffect, useMemo, useState } from 'react';

import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';

import {
    actionTypes,
    resetContent,
    setDirectMailContent,
    setPreHeader,
    setSenderProfileId,
    setSendChannel,
    setSmsContent,
    setSubjectLine,
    setTemplateId,
    setVoiceCallContent,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignChannelLabels, CampaignChannels } from 'domains/campaigns/types';
import { Template } from 'domains/content/types';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import AutocompleteContent from 'domains/core/components/AutocompleteContent';
import { ContentItem } from 'domains/core/components/AutocompleteContent/AutocompleteContent';
import FormLayout from 'domains/core/components/FormLayout';
import Select from 'domains/core/components/Select';
import useDirectMails from 'hooks/queries/useDirectMails';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useSmses from 'hooks/queries/useSmses';
import useTemplates from 'hooks/queries/useTemplates';
import useVoiceContentList from 'hooks/queries/useVoiceContentList';

import FileUploader from '../DirectMailContentStep/FileUploader';

type Props = {
    activeSendIndex: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
};

type MixedContentChannel =
    | CampaignChannels.EMAIL
    | CampaignChannels.SMS
    | CampaignChannels.DIRECT_MAIL
    | CampaignChannels.VOICE_CALL;

/**
 * Entry point for every send in a mixed-channel campaign: the user picks a piece of content —
 * not a channel — from a list for the selected channel, and the send's channel is derived from
 * whichever list the picked item came from. Once a channel is set, CustomCampaignContentStep
 * routes to that channel's normal content step for the rest of the fields (sender profile, etc).
 */
const CustomCampaignChooseSendContentStep = ({
    activeSendIndex,
    dispatchCampaignAction,
    setIsContinueEnabled,
}: Props) => {
    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates();
    const { data: smses = [], isLoading: isLoadingSmses } = useSmses();
    const { data: directMails = [], isLoading: isLoadingDirectMails } = useDirectMails();
    const { data: voiceCalls = [], isLoading: isLoadingVoiceCalls } = useVoiceContentList();
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();

    // Mirror the gating logic from CurationLandingPage:
    // email is always enabled; sms is opt-out (undefined → enabled);
    // directmail and voice are opt-in (undefined → disabled).
    const hasSMS = isFeatureSettingsSuccess && featureSettings?.sms_enabled !== false;
    const hasDM = isFeatureSettingsSuccess && !!featureSettings?.directmail_enabled;
    const hasVoice = isFeatureSettingsSuccess && !!featureSettings?.voice_enabled;

    const channelOptions = useMemo(() => {
        const opts: { label: string; value: MixedContentChannel }[] = [
            { label: CampaignChannelLabels[CampaignChannels.EMAIL], value: CampaignChannels.EMAIL },
        ];
        if (hasSMS) opts.push({ label: CampaignChannelLabels[CampaignChannels.SMS], value: CampaignChannels.SMS });
        if (hasDM)
            opts.push({
                label: CampaignChannelLabels[CampaignChannels.DIRECT_MAIL],
                value: CampaignChannels.DIRECT_MAIL,
            });
        if (hasVoice)
            opts.push({
                label: CampaignChannelLabels[CampaignChannels.VOICE_CALL],
                value: CampaignChannels.VOICE_CALL,
            });
        return opts;
    }, [hasSMS, hasDM, hasVoice]);

    const [searchInput, setSearchInput] = useState('');
    const [channelFilter, setChannelFilter] = useState<MixedContentChannel>(CampaignChannels.EMAIL);
    const [dmContentAction, setDmContentAction] = useState<'select' | 'upload'>('select');

    // Once feature settings load, snap the default selection to the first enabled channel
    // (handles the edge case where Email is disabled for this customer).
    useEffect(() => {
        if (channelOptions.length > 0 && !channelOptions.find((o) => o.value === channelFilter)) {
            setChannelFilter(channelOptions[0].value);
        }
    }, [channelOptions, channelFilter]);

    // Picking content immediately hands the send off to its channel-specific step (see
    // CustomCampaignContentStep), so this step never itself has a "complete" state to enable.
    useEffect(() => {
        setIsContinueEnabled(false);
    }, [setIsContinueEnabled]);

    const options = useMemo<AutocompleteOption<ContentItem>[]>(() => {
        const searchTerm = searchInput.trim().toLowerCase();

        const pools: Record<MixedContentChannel, ContentItem[]> = {
            [CampaignChannels.EMAIL]: templates,
            [CampaignChannels.SMS]: smses,
            [CampaignChannels.DIRECT_MAIL]: directMails,
            [CampaignChannels.VOICE_CALL]: voiceCalls,
        };
        const searchable = pools[channelFilter];
        const merged = searchTerm
            ? searchable.filter((item) => item.name.toLowerCase().includes(searchTerm))
            : searchable;

        // AutocompleteContent treats index 0 as a reserved "Create New" slot (see its
        // isCreateOption check), so real items must start at index 1 or the first real match
        // gets swallowed by that placeholder instead of being selectable.
        return merged.map((item, position) => ({
            index: position + 1,
            label: item.name,
            value: item,
        }));
    }, [templates, smses, directMails, voiceCalls, searchInput, channelFilter]);

    const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
        setSearchInput(value);
    };

    const handleChannelFilterChange = (event: any) => {
        setChannelFilter(event.target.value);
        setDmContentAction('select');
    };

    const handleDMToggleChange = (_e: any, value: string | null) => {
        if (!value) return;
        if (value === 'select') {
            dispatchCampaignAction(resetContent(activeSendIndex));
        }
        setDmContentAction(value as 'select' | 'upload');
    };

    // When FileUploader dispatches setDirectMailContent, also set the channel to DM first
    const wrappedDispatchForDMUpload = useCallback(
        (action: any) => {
            if (action.type === actionTypes.SET_DIRECT_MAIL_CONTENT) {
                dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.DIRECT_MAIL));
            }
            dispatchCampaignAction(action);
        },
        [dispatchCampaignAction, activeSendIndex]
    );

    const handleChange = (_event: React.SyntheticEvent, option: AutocompleteOption<ContentItem> | null) => {
        const item = option?.value;
        if (!item) return;

        if (templates.includes(item as Template)) {
            const template = item as Template;
            dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.EMAIL));
            dispatchCampaignAction(setTemplateId(template.id, activeSendIndex));
            dispatchCampaignAction(setSenderProfileId(template.senderProfileId ?? null, activeSendIndex));
            dispatchCampaignAction(setSubjectLine(template.subjectLine ?? '', activeSendIndex));
            dispatchCampaignAction(setPreHeader(template.preHeader ?? '', activeSendIndex));
            return;
        }

        if (smses.includes(item as any)) {
            dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.SMS));
            dispatchCampaignAction(setSmsContent({ contentId: item.id, contentName: item.name }, activeSendIndex));
            return;
        }

        if (voiceCalls.includes(item as any)) {
            dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.VOICE_CALL));
            dispatchCampaignAction(
                setVoiceCallContent({ contentId: item.id, contentName: item.name }, activeSendIndex)
            );
            return;
        }

        dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.DIRECT_MAIL));
        dispatchCampaignAction(setDirectMailContent({ contentId: item.id, contentName: item.name }, activeSendIndex));
    };

    return (
        <FormLayout
            header="select content"
            stepName="content"
            guidingText="Select the channel, then choose content — the send's channel is set automatically based on what you pick."
        >
            <Stack spacing={2} width="100%">
                <Select
                    data-testid="mixed-content-channel-filter"
                    label="Channel"
                    sx={{ textTransform: 'capitalize', width: '12rem' }}
                    value={channelFilter}
                    values={channelOptions}
                    onChange={handleChannelFilterChange}
                />
                {channelFilter === CampaignChannels.DIRECT_MAIL ? (
                    <>
                        <ToggleButtonGroup
                            value={dmContentAction}
                            exclusive
                            onChange={handleDMToggleChange}
                            aria-label="select or upload content"
                        >
                            <ToggleButton value="select" aria-label="select content">
                                Select
                            </ToggleButton>
                            <ToggleButton value="upload" aria-label="upload from computer">
                                Upload
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {dmContentAction === 'upload' ? (
                            <FileUploader
                                activeSendIndex={activeSendIndex}
                                dispatchCampaignAction={wrappedDispatchForDMUpload}
                                fileName=""
                            />
                        ) : (
                            <AutocompleteContent
                                options={options}
                                value={null}
                                onChange={handleChange}
                                onInputChange={handleInputChange}
                                textfieldInput={searchInput}
                                isLoading={isLoadingDirectMails}
                            />
                        )}
                    </>
                ) : (
                    <AutocompleteContent
                        options={options}
                        value={null}
                        onChange={handleChange}
                        onInputChange={handleInputChange}
                        textfieldInput={searchInput}
                        isLoading={isLoadingTemplates || isLoadingSmses || isLoadingDirectMails || isLoadingVoiceCalls}
                    />
                )}
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignChooseSendContentStep;
